import type { GqlSelectionListKey } from '../../selection-lists/types';

/** Geography taxonomy ids, as served by `taxonomyList(GEOGRAPHY)`. */
export const MSA_GEOGRAPHY_TAXONOMY_ID = 1;
export const ADMINISTRATIVE_GEOGRAPHY_TAXONOMY_ID = 2;

export const METRO_SELECTION_LIST_IDS: GqlSelectionListKey[] = [
  'region',
  'country',
  'msa',
  'city',
];

const ADMINISTRATIVE_SELECTION_LIST_IDS: GqlSelectionListKey[] = [
  'region',
  'country',
  'state',
  'county',
];

export const SELECTION_LIST_IDS_BY_GEOGRAPHY_TAXONOMY_ID: Record<
  number,
  GqlSelectionListKey[]
> = {
  [MSA_GEOGRAPHY_TAXONOMY_ID]: METRO_SELECTION_LIST_IDS,
  [ADMINISTRATIVE_GEOGRAPHY_TAXONOMY_ID]: ADMINISTRATIVE_SELECTION_LIST_IDS,
};

/**
 * The stored geography preference. Mirrors `taxonomies.role` / `.skill`:
 * a taxonomy id plus the version it was chosen under. `version` is omitted
 * when unknown, in which case the backend resolves latest — the behaviour
 * every geography tree query has always had.
 */
export type GeographyTaxonomyPreference = {
  taxonomyId: number;
  version?: string;
};

const DEFAULT_GEOGRAPHY_PREFERENCE: GeographyTaxonomyPreference = {
  taxonomyId: MSA_GEOGRAPHY_TAXONOMY_ID,
};

/**
 * Rows written before the taxonomy migration hold the string modes `'msa'` /
 * `'administrative'`, so `preferences[Geography]` can still be a string at
 * runtime even though the declared type no longer admits it. Every read goes
 * through here rather than trusting the type — the same defensive pattern as
 * `isColorModePreference`. Unrecognised values fall back to metro rather than
 * throwing, so a malformed row degrades to the default instead of a blank page.
 */
export const resolveGeographyPreference = (
  value: unknown
): GeographyTaxonomyPreference => {
  if (value === 'administrative') {
    return { taxonomyId: ADMINISTRATIVE_GEOGRAPHY_TAXONOMY_ID };
  }
  if (value === 'msa') {
    return { taxonomyId: MSA_GEOGRAPHY_TAXONOMY_ID };
  }
  if (typeof value === 'object' && value !== null) {
    const candidate = value as { taxonomyId?: unknown; version?: unknown };
    if (typeof candidate.taxonomyId === 'number') {
      return {
        taxonomyId: candidate.taxonomyId,
        ...(typeof candidate.version === 'string'
          ? { version: candidate.version }
          : {}),
      };
    }
  }
  return DEFAULT_GEOGRAPHY_PREFERENCE;
};

/**
 * Whether a geography taxonomy asks the backend to break geography down by
 * STATE rather than METRO AREA.
 *
 * This exists because the backend does not model geography as "which taxonomy";
 * it models one boolean, `geo_breakdown_state_over_msa`, plus a matching pair of
 * EntKinds. So a taxonomy id has to be reduced to that yes/no somewhere, and
 * doing it here keeps the mapping in ONE place rather than spreading
 * `taxonomyId === 2` across every request builder — which is also what makes a
 * third taxonomy a one-line change instead of a sweep.
 *
 * Takes the id rather than reading a hook so it can be called from plain
 * request-building code and asserted directly in tests.
 */
export const usesStateBreakdownOverMsa = (taxonomyId: number): boolean =>
  taxonomyId === ADMINISTRATIVE_GEOGRAPHY_TAXONOMY_ID;
